import { createApi } from "@reduxjs/toolkit/query/react";
import { baseAuthQuery } from "../base/baseApi";
import {
  DocumentTypeAttributeResponse,
  StudentStatusCertificateDocumentResponse,
  StudentStatusCertificateRequest,
  StudentTranscriptRequest,
  StudentDocumentResponse,
  StudentDiplomaRequest,
} from "../../models/dtos";
import { ApiResponse } from "../base/apiResponse";

export const studentDocumentsApi = createApi({
  reducerPath: "studentDocumentsApi",
  baseQuery: baseAuthQuery("studentDocuments"),
  tagTypes: ["StudentDocument", "StudentTranscript"],
  endpoints: (builder) => ({
    downloadDormitoryDeclaration: builder.query<Blob, { studentId: number | null }>({
      query: (params) => ({
        url: `${params.studentId}/dormitoryDeclaration`,
        method: "POST",
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
    }),
    downloadStudentDocumentProfile: builder.query<Blob, { studentId: number | null }>({
      query: (params) => ({
        url: `${params.studentId}/studentDocumentProfile`,
        method: "POST",
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
    }),
    downloadEducationContract: builder.query<Blob, { studentId: number | null }>({
      query: (params) => ({
        url: `${params.studentId}/plainEducationContract`,
        method: "POST",
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
    }),
    downloadHealthInsuranceDeclaration: builder.query<Blob, { studentId: number | null }>({
      query: (params) => ({
        url: `${params.studentId}/plainHealthInsurance`,
        method: "POST",
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
    }),
    downloadMainBook: builder.query<Blob, { studentId: number | null }>({
      query: (params) => ({
        url: `${params.studentId}/mainBook`,
        method: "POST",
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
    }),
    downloadStudentChecklist: builder.query<{ blob: Blob; fileName: string }, { studentId: number | null }>({
      query: (params) => ({
        url: `${params.studentId}/studentChecklist`,
        method: "GET",
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
    }),

    getDocumentTypeAttributes: builder.query<ApiResponse<DocumentTypeAttributeResponse[]>, number>({
      query: (documentTypeId) => ({
        url: `${documentTypeId}/documentTypeAttributes`,
        method: "GET",
      }),
    }),

    generateStudentStatusCertificate: builder.mutation<
      { blob: Blob; fileName: string },
      Partial<StudentStatusCertificateRequest>
    >({
      query: (request) => ({
        url: `generateStudentStatusCertificate`,
        method: "POST",
        body: request,
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
      invalidatesTags: ["StudentDocument"],
    }),

    updateStudentStatusCertificate: builder.mutation<
      { blob: Blob; fileName: string },
      Partial<StudentStatusCertificateRequest>
    >({
      query: (request) => ({
        url: `updateStudentStatusCertificate`,
        method: "POST",
        body: request,
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
      invalidatesTags: ["StudentDocument"],
    }),

    finalizeStudentDocument: builder.mutation<void, number>({
      query: (studentDocumentId) => ({
        url: `finalize/${studentDocumentId}`,
        method: "GET",
      }),
      invalidatesTags: ["StudentDocument"],
    }),

    deleteStudentStatusCertificate: builder.mutation<ApiResponse<boolean>, number>({
      query: (studentDocumentId) => ({
        url: `${studentDocumentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["StudentDocument"],
    }),

    getStudentStatusCertificates: builder.query<ApiResponse<StudentStatusCertificateDocumentResponse[]>, number>({
      query: (studentId) => ({
        url: `${studentId}/studentStatusCertificates`,
        method: "GET",
      }),
      providesTags: ["StudentDocument"],
    }),

    getStudentStatusCertificate: builder.query<{ blob: Blob; fileName: string }, number>({
      query: (studentDocumentId) => ({
        url: `${studentDocumentId}/studentStatusCertificate`,
        method: "GET",
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
      providesTags: ["StudentDocument"],
    }),

    getStudentTranscripts: builder.query<ApiResponse<StudentDocumentResponse[]>, number>({
      query: (studentId) => ({
        url: `${studentId}/studentTranscripts`,
        method: "GET",
      }),
      providesTags: ["StudentTranscript"],
    }),

    generateTranscriptOfRecords: builder.mutation<{ blob: Blob; fileName: string }, StudentTranscriptRequest>({
      query: (request) => ({
        url: `generateStudentTranscript`,
        method: "POST",
        body: request,
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
      invalidatesTags: ["StudentTranscript"],
    }),

    getTranscriptOfRecords: builder.query<{ blob: Blob; fileName: string }, { studentId: string }>({
      query: ({ studentId }) => ({
        url: `${studentId}/studentTranscript`,
        method: "GET",
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
    }),

    generateStudentDiploma: builder.mutation<{ blob: Blob; fileName: string }, StudentDiplomaRequest>({
      query: (request) => ({
        url: `generateStudentDiploma`,
        method: "POST",
        body: request,
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
    }),
  }),
});
